import { useState } from 'react';
import { MegaphoneIcon, MessageCircleIcon, UsersIcon, type LucideIcon } from 'lucide-react';
import { Button, IconBadge } from '@monksflow/monks-ui';

import { AnalysisDetailDialog, AnalysisMarkdown, AnalysisSubHeader } from '../../../components';
import type { CultureContextData } from '../CultureContext.types';
import { normalizeGeneration } from '../lib/normalizeCulture';
import { useTranslation } from 'react-i18next';

interface GenerationalPerspectivesProps {
  generationalInsights?: CultureContextData['generationalInsights'];
  memeMoments?: string[];
  socialMovements?: string[];
}

const GENERATIONS = [
  { key: 'genZ', labelKey: 'analysisResult.generationalPerspectives.genZ' },
  { key: 'millennial', labelKey: 'analysisResult.generationalPerspectives.millennial' },
  { key: 'genX', labelKey: 'analysisResult.generationalPerspectives.genX' },
  { key: 'boomer', labelKey: 'analysisResult.generationalPerspectives.boomer' },
] as const;

export const GenerationalPerspectives = ({
  generationalInsights,
  memeMoments,
  socialMovements,
}: GenerationalPerspectivesProps) => {
  const { t } = useTranslation('analysis');
  const [openCohort, setOpenCohort] = useState<(typeof GENERATIONS)[number]['key'] | null>(null);

  const cohorts = generationalInsights
    ? GENERATIONS.filter((gen) => Boolean(generationalInsights[gen.key]))
    : [];
  const hasMemes = Boolean(memeMoments?.length);
  const hasSocial = Boolean(socialMovements?.length);

  const openDetail = openCohort
    ? normalizeGeneration(generationalInsights?.[openCohort])
    : undefined;
  const openLabelKey = GENERATIONS.find((gen) => gen.key === openCohort)?.labelKey;
  const openCohortLabel = openLabelKey ? t(openLabelKey) : undefined;

  if (!cohorts.length && !hasMemes && !hasSocial) return null;

  return (
    <div className="flex flex-col gap-6">
      <AnalysisSubHeader
        title={t('analysisResult.generationalPerspectives.generationalPerspectives')}
        subtitle={t('analysisResult.generationalPerspectives.howEachCohortReadsTheBrand')}
        subtitleTone="meta"
      />

      {cohorts.length > 0 && (
        <div className="grid grid-cols-1 items-stretch gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {cohorts.map((gen) => {
            const detail = normalizeGeneration(generationalInsights?.[gen.key]);
            const portrait = detail?.imageUrl;

            return (
              <div
                key={gen.key}
                className="flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl"
              >
                {/* The design gives every card a portrait; reports written before the redesign
                    carry none, so the card closes up around it. */}
                {portrait && (
                  <img
                    src={portrait}
                    alt={t(gen.labelKey)}
                    className="aspect-square w-full rounded-full object-cover"
                  />
                )}
                <div className="flex flex-col gap-2">
                  <div className="flex flex-col gap-0.5">
                    <span className="text-body-xl font-medium text-foreground">
                      {t(gen.labelKey)}
                    </span>
                    {detail?.ageRange && (
                      <span className="text-body-sm text-foreground-subtle">{detail.ageRange}</span>
                    )}
                  </div>
                  <AnalysisMarkdown
                    text={detail?.description}
                    className="text-body-md text-foreground-alt"
                  />
                </div>
                {/* Only the redesigned cohorts carry paragraphs to open; an older report has
                    nothing behind the card, so it gets no control that opens an empty dialog. */}
                {detail?.expanded.length ? (
                  <div className="mt-auto pt-1">
                    <Button variant="outline" size="sm" onClick={() => setOpenCohort(gen.key)}>
                      {t('analysisResult.generationalPerspectives.readMore')}
                    </Button>
                  </div>
                ) : null}
              </div>
            );
          })}
        </div>
      )}

      <AnalysisDetailDialog
        open={openCohort !== null}
        onClose={() => setOpenCohort(null)}
        icon={<UsersIcon className="size-[17px]" />}
        title={openCohortLabel}
        /* The card's own paragraph, restated: extraction sends no separate per-cohort insight,
           and the AI overview leads its dialog with the card's line the same way. */
        insight={openDetail?.description}
      >
        <div className="flex flex-col gap-4">
          {openDetail?.expanded.map((paragraph, position) => (
            <AnalysisMarkdown
              key={`${position}-${paragraph}`}
              text={paragraph}
              className="text-base leading-6 text-foreground-alt"
            />
          ))}
        </div>
      </AnalysisDetailDialog>

      {(hasMemes || hasSocial) && (
        <div className="grid grid-cols-1 items-stretch gap-3 md:grid-cols-2">
          {hasMemes && (
            <BulletCard
              icon={MessageCircleIcon}
              title={t('analysisResult.generationalPerspectives.viralMomentsMemes')}
              items={memeMoments}
            />
          )}
          {hasSocial && (
            <BulletCard
              icon={MegaphoneIcon}
              title={t('analysisResult.generationalPerspectives.socialMovements')}
              items={socialMovements}
            />
          )}
        </div>
      )}
    </div>
  );
};

const BulletCard = ({
  icon: Icon,
  title,
  items,
}: {
  icon: LucideIcon;
  title: string;
  items?: string[];
}) => (
  <div className="flex h-full flex-col gap-3 rounded-lg border border-border-light p-xl">
    <IconBadge variant="secondary">
      <Icon />
    </IconBadge>
    <span className="text-body-xl font-medium text-foreground">{title}</span>
    <ul className="flex flex-col gap-3">
      {items?.map((item, i) => (
        <li key={`${item}-${i}`} className="flex items-start gap-3.5 pl-1.5">
          <span className="mt-[9px] size-1.5 shrink-0 rounded-full bg-foreground-subtle" />
          <AnalysisMarkdown text={item} className="text-body-md text-foreground-alt" />
        </li>
      ))}
    </ul>
  </div>
);
